import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan,
  faBars,
  faCalendar,
  faCalendarAlt,
  faCalendarCheck,
  faChartLine,
  faCog,
  faHistory,
  faList,
  faSignOutAlt,
  faStore,
  faTags,
  faThumbtack,
  faTimes,
  faUser,
  faUsers,
  faUserTie,
} from '@fortawesome/free-solid-svg-icons';
import { Link, useParams } from 'react-router-dom';
import { useAuthentication } from '@features/auth/useAuthentication';
import { useCurrentSalonStore } from '@stores/useCurrentSalonStore.ts';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface SubMenuItem {
  id: string;
  label: string;
  icon: IconDefinition;
  href: string;
  active?: boolean;
}

interface MenuSection {
  id: string;
  label: string;
  icon: IconDefinition;
  href?: string;
  items: SubMenuItem[];
}

interface AppMenuProps {
  className?: string;
}

const AppMenu: React.FC<AppMenuProps> = ({ className = '' }) => {
  const { loggedUser, logout } = useAuthentication();
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [isMinimized, setIsMinimized] = React.useState(false);
  const [isPinned, setIsPinned] = React.useState(true);
  const [isHovered, setIsHovered] = React.useState(false);
  const [_, setExpandedSections] = React.useState<Set<string>>(
    new Set(['dashboard', 'reservations']), // Výchozí rozbalené sekce
  );

  const { currentSalon } = useCurrentSalonStore();
  const { entityId: urlEntityId } = useParams<{ entityId?: string }>();
  // currentSalon hydrates after SalonSwitcher loads the salon list; relying
  // on it directly would briefly produce "/app/undefined/..." links right
  // after login. Prefer the URL param (we're already inside /app/[entityId])
  // and then the user's own accessRights as a stable fallback.
  const salonId = urlEntityId
    || currentSalon?.salonId
    || loggedUser?.accessRights?.salonId
    || null;

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const togglePin = () => {
    setIsPinned(!isPinned);
  };

  // Auto-hide and minimize logic - only for desktop
  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;

    // Only apply auto-hide on desktop (lg and up)
    const isDesktop = window.innerWidth >= 1024;

    if (!isDesktop) return;

    if (!isPinned && isHovered) {
      // Reset timeout when hovering and expand menu
      if (timeoutId) clearTimeout(timeoutId);
      setIsCollapsed(false);
      setIsMinimized(false);
    } else if (!isPinned && !isHovered) {
      // Start timeout when not hovering and not pinned
      timeoutId = setTimeout(() => {
        setIsMinimized(true);
        // Hide completely after longer timeout
        setTimeout(() => {
          setIsCollapsed(true);
        }, 500); // Hide completely after 0.5 seconds of being minimized
      }, 800); // Minimize after 0.8 seconds
    } else if (isPinned) {
      // When pinned, keep menu expanded and visible
      if (timeoutId) clearTimeout(timeoutId);
      setIsCollapsed(false);
      setIsMinimized(false);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isHovered, isPinned]);

  const allSections: (MenuSection & { requirePermission?: 'manageStaff' | 'manageServices' | 'manageSalon' | 'manageCustomers' | 'viewReports' })[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: faChartLine,
      href: `/app/${salonId}`,
      items: [
        /*{ id: 'calendar', label: 'Kalendář', icon: faCalendar, href: `/app/${salonId}` },*/
        /*{ id: 'today', label: 'Dnešní rezervace', icon: faCalendarDay, href: `/app/${salonId}/today` },
        { id: 'performance', label: 'Statistiky výkonu', icon: faChartBar, href: `/app/${salonId}/performance` },*/
        /*{ id: 'quick-actions', label: 'Rychlé akce', icon: faBolt, href: `/app/${salonId}/quick-actions` },*/
      ],
    },
    {
      id: 'reservations',
      label: 'Rezervace',
      icon: faCalendar,
      items: [
        { id: 'calendar', label: 'Kalendář rezervací', icon: faCalendarAlt, href: `/app/${salonId}/calendar`, active: true },
        /*{ id: 'new-reservation', label: 'Nová rezervace', icon: faPlus, href: `/app/${salonId}/new-reservation` },*/
        { id: 'history', label: 'Historie rezervací', icon: faHistory, href: `/app/${salonId}/history` },
        /*{ id: 'waiting-list', label: 'Čekací listy', icon: faClock, href: `/app/${salonId}/waiting-list` },*/
        { id: 'blocked-times', label: 'Blokované časy', icon: faBan, href: `/app/${salonId}/blocked-times` },
      ],
    },
    {
      id: 'customers',
      label: 'Zákazníci',
      icon: faUsers,
      requirePermission: 'manageCustomers',
      items: [
        { id: 'customer-list', label: 'Seznam zákazníků', icon: faList, href: `/app/${salonId}/customers` },
        /*{ id: 'new-customer', label: 'Nový zákazník', icon: faUserPlus, href: `/app/${salonId}/new-customer` },*/
        /*{ id: 'customer-history', label: 'Historie zákazníka', icon: faFileAlt, href: `/app/${salonId}/customer-history` },*/
        /*{ id: 'customer-notes', label: 'Poznámky', icon: faStickyNote, href: `/app/${salonId}/customer-notes` },*/
        /*{ id: 'loyalty-program', label: 'Loajální program', icon: faStar, href: `/app/${salonId}/loyalty-program` },*/
      ],
    },
    {
      id: 'staff',
      label: 'Zaměstnanci',
      icon: faUserTie,
      requirePermission: 'manageStaff',
      items: [
        { id: 'staff-list', label: 'Seznam zaměstnanců', icon: faUsers, href: `/app/${salonId}/staff` },
        /*{ id: 'working-hours', label: 'Pracovní doby', icon: faBusinessTime, href: `/app/${salonId}/working-hours` },
        { id: 'vacations', label: 'Dovolené a absence', icon: faPlane, href: `/app/${salonId}/vacations` },
        { id: 'commissions', label: 'Komise a odměny', icon: faCoins, href: `/app/${salonId}/commissions` },
        { id: 'performance', label: 'Výkonnost', icon: faChartLine, href: `/app/${salonId}/staff-performance` },*/
      ],
    },
    /*{
      id: 'finance',
      label: 'Financování',
      icon: faEuroSign,
      items: [
        { id: 'daily-revenue', label: 'Denní tržby', icon: faCashRegister, href: `/app/${salonId}/daily-revenue` },
        { id: 'staff-commissions', label: 'Komise zaměstnanců', icon: faPercentage, href: `/app/${salonId}/staff-commissions` },
        { id: 'invoices', label: 'Faktury a platby', icon: faFileInvoice, href: `/app/${salonId}/invoices` },
        { id: 'tax-reports', label: 'Daňové reporty', icon: faReceipt, href: `/app/${salonId}/tax-reports` },
        { id: 'revenue-distribution', label: 'Rozdělení výnosů', icon: faChartPie, href: `/app/${salonId}/revenue-distribution` },
      ],
    },*/
    /* {
      id: 'reports',
      label: 'Reporty',
      icon: faChartBar,
      items: [
        { id: 'monthly-overview', label: 'Měsíční přehledy', icon: faCalendarWeek, href: `/app/${salonId}/monthly-overview` },
        { id: 'service-analysis', label: 'Analýza služeb', icon: faCut, href: `/app/${salonId}/service-analysis` },
        { id: 'customer-analysis', label: 'Zákaznická analýza', icon: faUserAlt, href: `/app/${salonId}/customer-analysis` },
        { id: 'financial-reports', label: 'Finanční reporty', icon: faMoneyBillTrendUp, href: `/app/${salonId}/financial-reports` },
        { id: 'export-data', label: 'Export dat', icon: faDownload, href: `/app/${salonId}/export-data` },
      ],
    },*/
    {
      id: 'settings',
      label: 'Nastavení',
      icon: faCog,
      requirePermission: 'manageSalon',
      items: [
        { id: 'salon-profile', label: 'Profil salonu', icon: faStore, href: `/app/${salonId}/salon-profile` },
        { id: 'services-prices', label: 'Služby a ceny', icon: faTags, href: `/app/${salonId}/services-prices` },
        { id: 'service-categories', label: 'Kategorie služeb', icon: faTags, href: `/app/${salonId}/service-categories` },
        /*{ id: 'working-hours-settings', label: 'Pracovní doby', icon: faClock, href: `/app/${salonId}/working-hours-settings` },*/
        /*{ id: 'system-settings', label: 'Systémové nastavení', icon: faSlidersH, href: `/app/${salonId}/system-settings` },*/
        // { id: 'integrations', label: 'Integrace', icon: faPlug, href: `/app/${salonId}/integrations` },
      ],
    },
  ];

  const menuSections = allSections.filter(section => {
    if (!section.requirePermission) return true;
    switch (section.requirePermission) {
      case 'manageStaff':
        return loggedUser ? Boolean(loggedUser.accessRights?.canManageStaff) : false;
      case 'manageServices':
        return loggedUser ? Boolean(loggedUser.accessRights?.canManageServices) : false;
      case 'manageSalon':
        return loggedUser ? Boolean(loggedUser.accessRights?.canManageSalon) : false;
      case 'manageCustomers':
        return loggedUser ? Boolean(loggedUser.accessRights?.canManageCustomers) : false;
      case 'viewReports':
        return loggedUser ? Boolean(loggedUser.accessRights?.canViewReports) : false;
      default:
        return true;
    }
  });

  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button onClick={() => setIsCollapsed(!isCollapsed)} className="bg-white p-2 rounded-md shadow-md border border-gray-200">
          <FontAwesomeIcon icon={isCollapsed ? faBars : faTimes} className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Overlay for mobile */}
      {isCollapsed && <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30" onClick={() => setIsCollapsed(false)} />}

      {/* Auto-hide trigger zone - only on desktop when not pinned */}
      {!isPinned && <div className="hidden lg:block fixed left-0 top-0 w-4 h-full z-50" onMouseEnter={() => setIsHovered(true)} />}

      {/* Sidebar Content */}
      <aside
        className={`
        ${isCollapsed ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        fixed lg:static inset-y-0 left-0 z-40 bg-white shadow-lg border-r border-gray-200
        transition-all duration-300 ease-in-out lg:transition-all
        ${isMinimized ? 'w-20' : 'w-64'}
        ${className}
      `}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex flex-col max-h-dvh min-h-dvh">
          {/* Logo Section */}
          <div className="h-16 bg-green-600 flex items-center justify-between px-6 flex-shrink-0">
            {!isMinimized && (
              <div className="text-white text-xl font-semibold flex items-center">
                <FontAwesomeIcon icon={faCalendarCheck} className="mr-3" />
                Reservare
              </div>
            )}
            {isMinimized && (
              <div className="text-white text-xl font-semibold flex items-center justify-center w-full">
                <FontAwesomeIcon icon={faCalendarCheck} />
              </div>
            )}
            <button
              onClick={togglePin}
              className={`p-1 rounded transition-colors ${
                isPinned ? 'text-white bg-green-700 hover:bg-green-800' : 'text-green-200 hover:text-white hover:bg-green-700'
              }`}
              title={isPinned ? 'Odepnout menu' : 'Připnout menu'}
            >
              <FontAwesomeIcon icon={faThumbtack} className={`w-3 h-3 transition-transform ${isPinned ? 'rotate-45' : ''}`} />
            </button>
          </div>

          {/* Navigation Menu - Scrollable Area */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            {menuSections.map(section => {
              const isExpanded = true; // expandedSections.has(section.id);
              const hasHref = !!section.href;

              return (
                <div key={section.id} className="menu-section">
                  {hasHref ? (
                    // Sekce s href - chová se jako odkaz
                    <Link
                      to={section.href!}
                      className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors block"
                      title={isMinimized ? section.label : undefined}
                    >
                      <div className="flex items-center text-green-600 font-medium">
                        <FontAwesomeIcon icon={section.icon} className="mr-3 text-lg" />
                        {!isMinimized && section.label}
                      </div>
                    </Link>
                  ) : (
                    // Sekce bez href - chová se jako rozbalovací menu
                    <>
                      <div
                        className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => !isMinimized && toggleSection(section.id)}
                        title={isMinimized ? section.label : undefined}
                      >
                        <div className="flex items-center justify-between text-green-600 font-medium">
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={section.icon} className="mr-3 text-lg" />
                            {!isMinimized && section.label}
                          </div>
                          {/*{!isMinimized && <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} className="text-sm transition-transform duration-200" />}*/}
                        </div>
                      </div>

                      {!isMinimized && (
                        <div
                          className={`submenu pl-3 space-y-1 transition-all duration-300 overflow-hidden ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                        >
                          {section.items.map(item => (
                            <Link
                              key={item.id}
                              to={item.href}
                              className={`
                                block py-2 px-6 rounded-r-full transition-all cursor-pointer
                                ${item.active ? 'text-green-600 bg-green-50 font-medium' : 'text-gray-700 hover:text-green-600 hover:bg-green-50'}
                              `}
                            >
                              <FontAwesomeIcon icon={item.icon} className="mr-2" />
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {section.id !== 'settings' && !isMinimized && <div className="border-t border-gray-200"></div>}
                </div>
              );
            })}
          </div>

          {/* User Actions Section */}
          {loggedUser && (
            <div className="border-t border-gray-200 flex-shrink-0">
              {!isMinimized ? (
                <div className="p-4 space-y-2">
                  <Link to={salonId ? `/app/${salonId}/profile` : '#'} className="flex items-center px-3 py-2 text-gray-700 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors">
                    <FontAwesomeIcon icon={faUser} className="mr-3" />
                    Můj profil
                  </Link>
                  <button onClick={logout} className="w-full flex items-center px-3 py-2 text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    <FontAwesomeIcon icon={faSignOutAlt} className="mr-3" />
                    Odhlásit se
                  </button>
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  <Link
                    to={salonId ? `/app/${salonId}/profile` : '#'}
                    className="flex items-center justify-center p-2 text-gray-700 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Můj profil"
                  >
                    <FontAwesomeIcon icon={faUser} />
                  </Link>
                  <button
                    onClick={logout}
                    className="w-full flex items-center justify-center p-2 text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Odhlásit se"
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default AppMenu;
